import { inject, Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IdentityServiceBase } from '@seahorse/auth';
import { NotificationService, RouterUtil } from '@seahorse/common';
import { PreferenceDataService, PreferenceModel } from '@seahorse/domain';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SIDEBAR_LOCAL_STORAGE } from './sidebar/sidebar.model';

@Injectable({
  providedIn: 'root',
})
export class LayoutService implements OnDestroy {
  private sidebarToggleSource = new BehaviorSubject<boolean | null>(null);
  sidebarToggle$ = this.sidebarToggleSource.asObservable();

  showBaseLayout = true;
  private navigationReloadSource = new Subject<void>();
  navigationReload$ = this.navigationReloadSource.asObservable();

  sidebarOpen = false;
  private _preferencesService = inject(PreferenceDataService);
  private _identityService = inject(IdentityServiceBase);
  private _notificationService = inject(NotificationService);
  private _routerUtil = inject(RouterUtil);
  private subscriptions$ = new Subscription();

  constructor(private activatedRoute: ActivatedRoute, private router: Router) {
    this.subscriptions$.add(
      this.sidebarToggle$.subscribe((currentValue) => {
        this.sidebarOpen = !!currentValue;
      })
    );

    this.initializeSidebarOpen();
    window.addEventListener('storage', this.handleStorageChange.bind(this));

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.handleRouterEvent();
      });

    // First time check
    this.handleRouterEvent();
  }

  toggleSidebar(sidebarOpen: boolean) {
    this.sidebarToggleSource.next(sidebarOpen);
  }

  private handleRouterEvent() {
    this._routerUtil.extractModuleNameFromRoute(this.activatedRoute);

    const showBase = this._routerUtil.getDataElementFromRoute(
      this.activatedRoute,
      'ui.showBaseLayout',
      true
    );

    if (showBase !== null) {
      this.showBaseLayout = showBase;
    } else {
      this.showBaseLayout = true;
    }
  }

  reloadNavigation() {
    this.navigationReloadSource.next();
  }

  toggleSidebarOpen() {
    const newValue = !this.sidebarOpen;
    localStorage.setItem(SIDEBAR_LOCAL_STORAGE.isOpen, newValue.toString());

    const event = new StorageEvent('storage', {
      key: SIDEBAR_LOCAL_STORAGE.isOpen,
      newValue: newValue.toString(),
      oldValue: (!newValue).toString(),
      storageArea: localStorage,
      url: window.location.href,
    });
    this.handleStorageChange(event);

    const sidebarOpenPreference = {
      name: 'sidebarOpen',
      fieldType: 3,
      fieldValue: newValue,
      category: 'layout',
    } as PreferenceModel;

    this._preferencesService.save(sidebarOpenPreference).subscribe({
      next: (response) => {
        if (response.hasResult) {
          this._identityService.setPreferences([response.result]);
        } else {
          this._notificationService.displayErrorNotification(response.messages);
        }
      },
      error: (e) => this._notificationService.displayErrorNotification(e),
    });
  }

  private initializeSidebarOpen() {
    const sidebarOpenLocalStorage = localStorage.getItem(
      SIDEBAR_LOCAL_STORAGE.isOpen
    );
    let initialSidebarOpen: boolean;

    if (
      sidebarOpenLocalStorage === null ||
      sidebarOpenLocalStorage === undefined
    ) {
      const sidebarOpenPreference =
        this._identityService
          .getPreferences()
          ?.find((x) => x.name === 'sidebarOpen' && x.category === 'layout')
          ?.fieldValue?.toString()
          .toLowerCase() === 'true';

      initialSidebarOpen = sidebarOpenPreference ?? true;
      localStorage.setItem(
        SIDEBAR_LOCAL_STORAGE.isOpen,
        initialSidebarOpen.toString()
      );
    } else {
      initialSidebarOpen = sidebarOpenLocalStorage === 'true';
    }

    this.toggleSidebar(initialSidebarOpen);
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key === SIDEBAR_LOCAL_STORAGE.isOpen) {
      this.toggleSidebar(event.newValue === 'true');
    }
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }
}
